<template>
  <div class="card border-0 shadow-none rounded-3 mb-4">
    <div class="card-body p-4 p-xl-40">
      <div class="toolbar-card mb-4">
        <div class="toolbar-left">
          <h4 class="mb-1">{{ $t("size_page.list_title") }}</h4>
          <p class="text-muted mb-0">
            {{ $t("size_page.list_description") }}
          </p>
        </div>
        <div class="toolbar-right">
          <div class="search-shell">
            <input
              :value="searchQuery"
              type="text"
              class="form-control toolbar-search"
              :placeholder="$t('size_page.search_placeholder')"
              @input="$emit('update:searchQuery', ($event.target as HTMLInputElement).value)"
            />
            <i class="ri-search-line search-icon"></i>
          </div>
          <button class="btn style-one btn-sm d-flex align-items-center gap-2" @click="$emit('create')">
            <i class="ri-add-circle-line"></i>
            <span>{{ $t("size_page.create_button") }}</span>
          </button>
        </div>
      </div>

      <div class="stats-grid mb-4">
        <div class="stat-card">
          <span class="stat-label">{{ $t("size_page.total_sizes") }}</span>
          <strong class="stat-value">{{ stats.total }}</strong>
        </div>
        <div class="stat-card">
          <span class="stat-label">{{ $t("size_page.status_active") }}</span>
          <strong class="stat-value text-success">{{ stats.active }}</strong>
        </div>
        <div class="stat-card">
          <span class="stat-label">{{ $t("size_page.status_inactive") }}</span>
          <strong class="stat-value text-danger">{{ stats.inactive }}</strong>
        </div>
      </div>

      <a-table
        :columns="columns"
        :data-source="sizes"
        :row-key="'id'"
        :pagination="paginationConfig"
        :loading="loading"
        :locale="{ emptyText: $t('size_page.no_sizes_found') }"
        class="size-table"
      >
        <template #bodyCell="{ column, record, text }">
          <template v-if="column.key === 'code'">
            <span class="code-pill">{{ text || $t("size_page.not_available") }}</span>
          </template>
          <template v-else-if="column.key === 'description'">
            <span class="text-muted">{{ text || $t("size_page.no_description") }}</span>
          </template>
          <template v-else-if="column.key === 'status'">
            <span class="status-badge" :class="isActive(record.status) ? 'status-active' : 'status-inactive'">
              {{ isActive(record.status) ? $t("size_page.status_active") : $t("size_page.status_inactive") }}
            </span>
          </template>
          <template v-else-if="column.key === 'actions'">
            <div class="d-flex justify-content-center gap-2">
              <button class="icon-btn" :title="$t('size_page.edit')" @click="$emit('edit', record)">
                <i class="ri-pencil-line"></i>
              </button>
              <button class="icon-btn danger" :title="$t('size_page.delete')" @click="$emit('delete', record)">
                <i class="ri-delete-bin-6-line"></i>
              </button>
            </div>
          </template>
          <template v-else>
            {{ text || $t("size_page.not_available") }}
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import { useI18n } from "vue-i18n";
import type { Size } from "@/services/size";

export default defineComponent({
  name: "SizeList",
  emits: ["update:searchQuery", "create", "edit", "delete"],
  props: {
    sizes: {
      type: Array as PropType<Size[]>,
      required: true,
    },
    searchQuery: {
      type: String,
      default: "",
    },
    paginationConfig: {
      type: Object as PropType<Record<string, unknown>>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    stats: {
      type: Object as PropType<{ total: number; active: number; inactive: number }>,
      required: true,
    },
  },
  setup() {
    const { t } = useI18n();
    const columns = computed(() => [
      { title: t("size_page.table_code"), dataIndex: "code", key: "code", align: "center" as const, width: 160 },
      { title: t("size_page.table_name"), dataIndex: "name", key: "name", align: "center" as const, width: 220 },
      { title: t("size_page.table_description"), dataIndex: "description", key: "description", align: "center" as const, width: 340 },
      { title: t("size_page.table_status"), dataIndex: "status", key: "status", align: "center" as const, width: 140 },
      { title: t("size_page.table_action"), key: "actions", align: "center" as const, width: 140 },
    ]);

    const isActive = (status?: boolean | number) => String(status ?? 1) !== "0" && String(status ?? 1) !== "false";

    return {
      columns,
      isActive,
    };
  },
});
</script>

<style scoped>
.toolbar-card {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 18px;
  padding: 22px;
  border: 1px solid #edf0f7;
  border-radius: 20px;
  background: #fbfcff;
}

.toolbar-right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.stat-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 18px 20px;
  border: 1px solid #edf0f7;
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.stat-label {
  color: #7d8aa2;
  font-size: 13px;
  font-weight: 600;
}

.stat-value {
  color: #0f172a;
  font-size: 26px;
  line-height: 1;
}

.search-shell {
  position: relative;
  min-width: 340px;
}

.toolbar-search {
  min-height: 54px;
  padding: 14px 48px 14px 18px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  box-shadow: none;
}

.toolbar-search:focus {
  border-color: #605dff;
  box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12);
}

.search-icon {
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: #7d8aa2;
  pointer-events: none;
}

.size-table :deep(.ant-table-thead > tr > th) {
  font-weight: 700;
}

.code-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 88px;
  padding: 6px 12px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4f46e5;
  font-weight: 700;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 88px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-active {
  background: #e8f7ee;
  color: #1f9254;
}

.status-inactive {
  background: #fdecec;
  color: #d33c43;
}

.icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid #e3e8f5;
  background: #fff;
  color: #4f46e5;
}

.icon-btn.danger {
  color: #d33c43;
}

@media (max-width: 991px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .search-shell {
    min-width: 100%;
  }
}
</style>
